import { z } from 'zod';
import { ProductLineSchema, ProjectResponseSchema, VoiceProviderSchema } from './Project.js';
import { ProjectSettingsResponseSchema, ResolvedProjectContentSchema } from './Config.js';
import { ShareablePreviewResponseSchema } from './ShareablePreview.js';
import { QuestionMediaSchema, QuestionTypeSchema } from './common.js';
export const Bcp47LocaleSchema = z
    .string()
    .trim()
    .min(2)
    .max(35)
    .refine((value) => {
    try {
        void new Intl.Locale(value);
        return true;
    }
    catch {
        return false;
    }
}, 'Invalid locale tag');
export const LocaleFallbackModeSchema = z.enum(['none', 'language', 'default', 'language-default']);
export const ProjectStateViewSchema = z.enum(['resolved', 'all']);
export const LocaleStateQuerySchema = z
    .object({
    locale: Bcp47LocaleSchema.optional(),
    fallback: LocaleFallbackModeSchema.prefault('none'),
    view: ProjectStateViewSchema.prefault('resolved'),
})
    .strict();
export const LocaleResolutionSchema = z
    .object({
    requestedLocale: Bcp47LocaleSchema.nullable(),
    resolvedLocale: Bcp47LocaleSchema,
    defaultLocale: Bcp47LocaleSchema,
    availableLocales: z.array(Bcp47LocaleSchema),
    fallbackMode: LocaleFallbackModeSchema,
    fallbackApplied: z.boolean(),
})
    .strict();
export const ProjectStateResponseSchema = z
    .object({
    project: ProjectResponseSchema,
    content: ResolvedProjectContentSchema,
    settings: ProjectSettingsResponseSchema,
    locale: LocaleResolutionSchema,
})
    .strict();
export const PublicProjectStateResponseSchema = z
    .object({
    projectId: z.string(),
    organizationId: z.string(),
    publicId: z.string(),
    configVersion: z.number().multipleOf(1).nonnegative(),
    content: ResolvedProjectContentSchema,
    settings: ProjectSettingsResponseSchema,
    locale: LocaleResolutionSchema,
})
    .strict();
export const ShareablePreviewStateResponseSchema = z
    .object({
    preview: ShareablePreviewResponseSchema,
    project: z.object({
        id: z.string(),
        organizationId: z.string(),
        title: z.string(),
        description: z.string().nullable(),
        status: z.string(),
        /** Carried so the participant page words a check-in preview as a check-in. */
        systemKind: z.enum(['listening_pulse']).nullable().optional(),
        /** Picks the assistant profile for a preview, so a check-in gets the check-in agent. */
        productLine: ProductLineSchema.optional(),
        /** Picks the voice vendor a preview is provisioned on; omitted means VAPI. */
        voiceProvider: VoiceProviderSchema.optional(),
    }),
    content: ResolvedProjectContentSchema,
    settings: ProjectSettingsResponseSchema,
    locale: LocaleResolutionSchema,
})
    .strict();
export const RevisionAuthorSchema = z
    .object({
    actorType: z.enum(['user', 'ai', 'system']),
    actorId: z.string().trim().min(1).max(255),
    origin: z.enum(['user', 'ai', 'system']),
    traceId: z.string().trim().min(1).max(255).optional(),
})
    .strict();
export const QuestionCorePatchSchema = z
    .object({
    questionType: QuestionTypeSchema.optional(),
    required: z.boolean().optional(),
    followUpCount: z.number().multipleOf(1).min(0).max(10).optional(),
    media: QuestionMediaSchema.nullable().optional(),
    expectedRevision: z.number().multipleOf(1).nonnegative().optional(),
    author: RevisionAuthorSchema,
})
    .strict();
export const QuestionLocalePatchSchema = z
    .object({
    text: z.string().trim().min(1),
    options: z.array(z.string()).optional(),
    followUpGuidance: z.string().nullable().optional(),
    theme: z.string().nullable().optional(),
    expectedRevision: z.number().multipleOf(1).nonnegative().optional(),
    author: RevisionAuthorSchema,
})
    .strict();
export const FieldLocalePatchSchema = z
    .object({
    value: z.unknown().optional(),
    expectedRevision: z.number().multipleOf(1).nonnegative().optional(),
    author: RevisionAuthorSchema,
})
    .strict();
export const PutQuestionsQuestionLocaleInputSchema = z
    .object({
    text: z.string().trim().min(1),
    options: z.array(z.string()).optional(),
    followUpGuidance: z.string().nullable().optional(),
    theme: z.string().nullable().optional(),
})
    .strict();
export const PutQuestionsQuestionInputSchema = z
    .object({
    questionId: z.string().guid().optional(),
    core: z
        .object({
        questionType: QuestionTypeSchema.optional(),
        required: z.boolean().optional(),
        followUpCount: z.number().multipleOf(1).min(0).max(10).optional(),
        media: QuestionMediaSchema.nullable().optional(),
    })
        .strict()
        .optional(),
    locales: z.record(Bcp47LocaleSchema, PutQuestionsQuestionLocaleInputSchema).optional(),
})
    .strict();
export const PutProjectQuestionsRequestSchema = z
    .object({
    questions: z.array(PutQuestionsQuestionInputSchema),
    expectedRevision: z.number().multipleOf(1).nonnegative().optional(),
    author: RevisionAuthorSchema,
})
    .strict();
export const PostProjectQuestionRequestSchema = z
    .object({
    question: PutQuestionsQuestionInputSchema,
    position: z.number().multipleOf(1).nonnegative().optional(),
    expectedRevision: z.number().multipleOf(1).nonnegative().optional(),
    author: RevisionAuthorSchema,
})
    .strict();
export const DeleteProjectQuestionRequestSchema = z
    .object({
    expectedRevision: z.number().multipleOf(1).nonnegative().optional(),
    author: RevisionAuthorSchema,
})
    .strict();
export const PutProjectLocaleQuestionsRequestSchema = z
    .object({
    questions: z.array(z
        .object({
        questionId: z.string().guid(),
        text: z.string().trim().min(1),
        options: z.array(z.string()).optional(),
        followUpGuidance: z.string().nullable().optional(),
        theme: z.string().nullable().optional(),
        expectedRevision: z.number().multipleOf(1).nonnegative().optional(),
    })
        .strict()),
    author: RevisionAuthorSchema,
})
    .strict();
export const UpdateDefaultLocaleRequestSchema = z
    .object({
    locale: Bcp47LocaleSchema,
    author: RevisionAuthorSchema,
})
    .strict();
export const CreateProjectLocaleRequestSchema = z
    .object({
    locale: Bcp47LocaleSchema,
    moderatorId: z.string().trim().min(1).optional(),
    author: RevisionAuthorSchema,
})
    .strict();
export const UpdateProjectLocaleRequestSchema = z
    .object({
    moderatorId: z.string().trim().min(1),
    author: RevisionAuthorSchema,
})
    .strict();
export const PatchProjectStateRequestSchema = z
    .object({
    author: RevisionAuthorSchema,
    defaultLocale: Bcp47LocaleSchema.optional(),
    fieldLocales: z
        .array(z
        .object({
        fieldKey: z.string().trim().min(1).max(100),
        locale: Bcp47LocaleSchema,
        value: z.unknown().optional(),
        expectedRevision: z.number().multipleOf(1).nonnegative().optional(),
    })
        .strict())
        .optional(),
    questionCores: z
        .array(z
        .object({
        questionId: z.string().guid(),
        questionType: QuestionTypeSchema.optional(),
        required: z.boolean().optional(),
        followUpCount: z.number().multipleOf(1).min(0).max(10).optional(),
        media: QuestionMediaSchema.nullable().optional(),
        expectedRevision: z.number().multipleOf(1).nonnegative().optional(),
    })
        .strict())
        .optional(),
    questionLocales: z
        .array(z
        .object({
        questionId: z.string().guid(),
        locale: Bcp47LocaleSchema,
        text: z.string().trim().min(1),
        options: z.array(z.string()).optional(),
        followUpGuidance: z.string().nullable().optional(),
        theme: z.string().nullable().optional(),
        expectedRevision: z.number().multipleOf(1).nonnegative().optional(),
    })
        .strict())
        .optional(),
    structure: z
        .object({
        questions: z.array(PutQuestionsQuestionInputSchema),
        expectedRevision: z.number().multipleOf(1).nonnegative().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
export const ProjectStructureUpdateResponseSchema = z
    .object({
    projectId: z.string().guid(),
    projectVersion: z.number().multipleOf(1).nonnegative(),
    structureRevision: z.number().multipleOf(1).nonnegative(),
    questionIds: z.array(z.string().guid()),
})
    .strict();
export const QuestionCoreMutationResponseSchema = z
    .object({
    projectId: z.string().guid(),
    questionId: z.string().guid(),
    projectVersion: z.number().multipleOf(1).nonnegative(),
    revision: z.number().multipleOf(1).nonnegative(),
    questionType: QuestionTypeSchema,
    required: z.boolean(),
    followUpCount: z.number().multipleOf(1).nonnegative(),
    media: QuestionMediaSchema.optional(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const QuestionLocaleMutationResponseSchema = z
    .object({
    projectId: z.string().guid(),
    questionId: z.string().guid(),
    locale: Bcp47LocaleSchema,
    projectVersion: z.number().multipleOf(1).nonnegative(),
    revision: z.number().multipleOf(1).nonnegative(),
    text: z.string(),
    options: z.array(z.string()),
    followUpGuidance: z.string().nullable(),
    theme: z.string().nullable(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const FieldLocaleMutationResponseSchema = z
    .object({
    projectId: z.string().guid(),
    fieldKey: z.string(),
    locale: Bcp47LocaleSchema,
    projectVersion: z.number().multipleOf(1).nonnegative(),
    revision: z.number().multipleOf(1).nonnegative(),
    value: z.unknown().optional(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const LocaleQuestionsBulkMutationResponseSchema = z
    .object({
    projectId: z.string().guid(),
    locale: Bcp47LocaleSchema,
    projectVersion: z.number().multipleOf(1).nonnegative(),
    updated: z.number().multipleOf(1).nonnegative(),
})
    .strict();
export const DefaultLocaleMutationResponseSchema = z
    .object({
    projectId: z.string().guid(),
    defaultLocale: Bcp47LocaleSchema,
    availableLocales: z.array(Bcp47LocaleSchema),
    items: z
        .array(z
        .object({
        locale: Bcp47LocaleSchema,
        isDefault: z.boolean(),
        moderatorId: z.string().trim().min(1),
    })
        .strict())
        .prefault([]),
    updatedAt: z.string().datetime(),
})
    .strict();
export const ProjectModeratorsResponseSchema = z
    .object({
    projectId: z.string().guid(),
    moderatorIds: z.array(z.string().trim().min(1)),
    items: z
        .array(z
        .object({
        locale: Bcp47LocaleSchema,
        isDefault: z.boolean(),
        moderatorId: z.string().trim().min(1),
    })
        .strict())
        .prefault([]),
    updatedAt: z.string().datetime(),
})
    .strict();
export const ReplaceProjectModeratorsRequestSchema = z
    .object({
    moderatorIds: z.array(z.string().trim().min(1)),
    author: RevisionAuthorSchema,
})
    .strict();
export const AddProjectModeratorRequestSchema = z
    .object({
    moderatorId: z.string().trim().min(1),
    position: z.number().multipleOf(1).nonnegative().optional(),
    author: RevisionAuthorSchema,
})
    .strict();
export const RemoveProjectModeratorRequestSchema = z
    .object({
    author: RevisionAuthorSchema,
})
    .strict();
export const PatchProjectStateResponseSchema = z
    .object({
    projectId: z.string().guid(),
    projectVersion: z.number().multipleOf(1).nonnegative(),
    structureRevision: z.number().multipleOf(1).nonnegative(),
    touched: z
        .object({
        defaultLocale: z.boolean(),
        fields: z.number().multipleOf(1).nonnegative(),
        questionCores: z.number().multipleOf(1).nonnegative(),
        questionLocales: z.number().multipleOf(1).nonnegative(),
        structure: z.boolean(),
    })
        .strict(),
})
    .strict();
export const AddProjectQuestionResponseSchema = z
    .object({
    projectId: z.string().guid(),
    questionId: z.string().guid(),
    position: z.number().multipleOf(1).nonnegative(),
    projectVersion: z.number().multipleOf(1).nonnegative(),
    structureRevision: z.number().multipleOf(1).nonnegative(),
    questionIds: z.array(z.string().guid()),
})
    .strict();
export const DeleteProjectQuestionResponseSchema = z
    .object({
    projectId: z.string().guid(),
    questionId: z.string().guid(),
    projectVersion: z.number().multipleOf(1).nonnegative(),
    structureRevision: z.number().multipleOf(1).nonnegative(),
    questionIds: z.array(z.string().guid()),
})
    .strict();
export const QuestionCoreStateSchema = z
    .object({
    revision: z.number().multipleOf(1).nonnegative(),
    questionType: QuestionTypeSchema,
    required: z.boolean(),
    followUpCount: z.number().multipleOf(1).nonnegative(),
    media: QuestionMediaSchema.optional(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const QuestionLocaleStateSchema = z
    .object({
    revision: z.number().multipleOf(1).nonnegative(),
    text: z.string(),
    options: z.array(z.string()),
    followUpGuidance: z.string().nullable(),
    theme: z.string().nullable(),
    author: RevisionAuthorSchema.optional(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const FieldLocaleStateSchema = z
    .object({
    revision: z.number().multipleOf(1).nonnegative(),
    value: z.unknown().optional(),
    author: RevisionAuthorSchema.optional(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const ProjectStateAllResponseSchema = z
    .object({
    project: ProjectResponseSchema,
    projectVersion: z.number().multipleOf(1).nonnegative(),
    structureRevision: z.number().multipleOf(1).nonnegative(),
    locales: z
        .object({
        defaultLocale: Bcp47LocaleSchema,
        available: z.array(Bcp47LocaleSchema),
        items: z
            .array(z
            .object({
            locale: Bcp47LocaleSchema,
            isDefault: z.boolean(),
            moderatorId: z.string().trim().min(1),
        })
            .strict())
            .prefault([]),
    })
        .strict(),
    fields: z.record(z.string(), z.record(Bcp47LocaleSchema, FieldLocaleStateSchema)),
    questions: z.array(z
        .object({
        questionId: z.string().guid(),
        position: z.number().multipleOf(1).nonnegative(),
        core: QuestionCoreStateSchema,
        locales: z.record(Bcp47LocaleSchema, QuestionLocaleStateSchema),
    })
        .strict()),
})
    .strict();
export const QuestionMediaUploadTicketRequestSchema = z
    .object({
    fileName: z.string().trim().min(1).max(255).optional(),
    contentType: z.string().trim().min(1).max(255).optional(),
})
    .strict();
export const QuestionMediaUploadTicketResponseSchema = z
    .object({
    projectId: z.string().guid(),
    questionId: z.string().guid(),
    blobPath: z.string(),
    uploadUrl: z.string(),
    publicUrl: z.string(),
})
    .strict();
export const FinalizeQuestionMediaRequestSchema = z
    .object({
    blobPath: z.string().min(1),
})
    .strict();
export const FinalizeQuestionMediaResponseSchema = z
    .object({
    blobPath: z.string(),
    publicUrl: z.string(),
    contentType: z.string().optional(),
    byteSize: z.number().multipleOf(1).nonnegative().optional(),
})
    .strict();
//# sourceMappingURL=V2State.js.map